/**
 * Typed wrapper around the PHP endpoints in `public/api/`.
 *
 * Every mutating call carries the CSRF token that PHP stamped into a <meta>
 * tag, so the server-side `Csrf::verify()` succeeds. Centralising that here
 * means no caller can forget it.
 */

export interface CartLineDTO {
  id: number
  slug: string
  name: string
  brand: string
  gradient: string
  price: number
  quantity: number
  line_total: number
}

export interface CartStateDTO {
  count: number
  subtotal: number
  lines: CartLineDTO[]
  free_shipping_remaining: number
  free_shipping_threshold: number
}

export interface WishlistStateDTO {
  count: number
  ids: number[]
  added?: boolean
}

export interface SuggestionDTO {
  slug: string
  name: string
  brand: string
  gradient: string
  price: number
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

/**
 * Sub-directory the store is served from, emitted by the PHP layout. Empty
 * when `public/` is the document root, so root-relative paths still work.
 */
export function basePath(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="base-path"]')?.content ?? ''
}

/** Prefix an internal root-absolute path with the mount point. */
export function withBase(path: string): string {
  return basePath() + path
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(withBase(url), {
    ...init,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'fetch',
      'X-CSRF-Token': csrfToken(),
      ...init.headers,
    },
  })

  // Read the body once, then decide — reading twice throws.
  const text = await response.text()
  let data: unknown = null
  try {
    data = text === '' ? null : JSON.parse(text)
  } catch {
    throw new ApiError('The server returned an unexpected response.', response.status)
  }

  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String(data.error)
        : 'Something went wrong. Please try again.'
    throw new ApiError(message, response.status)
  }

  return data as T
}

function form(payload: Record<string, string | number>): URLSearchParams {
  const body = new URLSearchParams()
  Object.entries(payload).forEach(([key, value]) => body.set(key, String(value)))

  return body
}

export const api = {
  addToCart(productId: number, quantity = 1) {
    return request<CartStateDTO & { message: string }>('/api/cart.php?action=add', {
      method: 'POST',
      body: form({ product_id: productId, quantity }),
    })
  },

  updateCartQuantity(productId: number, quantity: number) {
    return request<CartStateDTO>('/api/cart.php?action=update', {
      method: 'POST',
      body: form({ product_id: productId, quantity }),
    })
  },

  removeFromCart(productId: number) {
    return request<CartStateDTO>('/api/cart.php?action=remove', {
      method: 'POST',
      body: form({ product_id: productId }),
    })
  },

  getCart() {
    return request<CartStateDTO>('/api/cart.php?action=state')
  },

  toggleWishlist(productId: number) {
    return request<WishlistStateDTO & { message: string }>('/api/wishlist.php', {
      method: 'POST',
      body: form({ product_id: productId }),
    })
  },

  suggest(term: string) {
    return request<{ results: SuggestionDTO[] }>(`/api/search.php?q=${encodeURIComponent(term)}`)
  },
}
